import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type PublicUser } from '@/lib/api'

// ─────────────────────────────────────────────────────────────────────────────
// THE SIGNED-IN SESSION, CLIENT SIDE.
//
// ONE source of truth: react-query holds the result of /api/auth/me. Every
// component reads it through `useSession()`, so two places on the same page can
// never disagree about who is signed in.
//
// Nothing is stored in localStorage. The session lives in an HttpOnly cookie
// that JavaScript cannot read — that is exactly what stops an XSS bug from
// walking off with the account.
// ─────────────────────────────────────────────────────────────────────────────

interface SessionValue {
  user: PublicUser | null
  loading: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: (credential?: string, profile?: { email?: string; name?: string; avatar?: string; googleId?: string }) => Promise<void>
  register: (email: string, password: string, name?: string) => Promise<void>
  logout: () => Promise<void>
  /** Re-read /api/auth/me — call it after changing the profile. */
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export const SESSION_KEY = ['auth', 'me'] as const

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return (await api.auth.me()).user
      } catch (e) {
        // Database not configured (503) or the Worker not up yet: "nobody is
        // signed in" is the right answer, not an error screen.
        if (e instanceof ApiError) return null
        throw e
      }
    },
    staleTime: 60_000,
    retry: false,
  })

  const value = useMemo<SessionValue>(() => {
    const user = data ?? null
    // A `{}` block, not an expression: setQueryData RETURNS the value it just
    // set, so the shorthand would infer these functions as Promise<unknown>
    // instead of Promise<void>.
    const set = (next: PublicUser | null) => {
      qc.setQueryData(SESSION_KEY, next)
    }
    return {
      user,
      loading: isLoading,
      isAdmin: user?.role === 'admin',
      login: async (email, password) => set((await api.auth.login(email, password)).user),
      loginWithGoogle: async (credential, profile) => set((await api.auth.google(credential, profile)).user),
      register: async (email, password, name) => set((await api.auth.register(email, password, name)).user),
      logout: async () => {
        await api.auth.logout().catch(() => undefined)
        set(null)
        // Data belonging to whoever just signed out must not stay in the cache
        // for the next person to sign in to see.
        await qc.invalidateQueries()
      },
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: SESSION_KEY })
      },
    }
  }, [data, isLoading, qc])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider> — see src/main.tsx')
  return ctx
}
